/**
 * Build the absolute public URL that the user will click to open a
 * shared chat session.
 *
 * The link MUST point at the Flowenti SPA (the React app), not at the
 * backend's REST endpoint. The SPA then serves the route
 * `/ai/shared/:slug` (added to `App.tsx`), which in turn fetches the
 * actual session data from
 * `${BE_ORIGIN}/ai/shared/<slug>` (see `buildSharedApiUrl` for how
 * `BE_ORIGIN` is derived from `REACT_APP_STATS_BACKEND_BASE_URI`).
 * The slug is only "alive" on the BE after `useLLM.shareSession`
 * has issued the authenticated POST `/ai/sessions/.../share` that
 * activates the share (the user does that by clicking the Share
 * button in the AI drawer — visiting `/ai/shared/<slug>` alone does
 * not activate it).
 *
 * The backend may return the share target in any of these shapes:
 *   - a bare slug:                        "fRgAKKE16BmPUFNJidLRpA"
 *   - a relative slug with leading slash: "/fRgAKKE16BmPUFNJidLRpA"
 *   - a BE absolute URL:                  "https://bp2.flowenti.com/helpers/fRgA…"
 *   - an FE absolute URL:                 "https://www.flowenti.com/ai/shared/fRgA…"
 *
 * The function extracts the slug from any of these and always emits
 * a URL of the form `${SPA_BASE}/#/ai/shared/<slug>`. We never
 * trust a BE absolute URL as the final destination because the BE
 * does not know where the FE is hosted.
 *
 * The SPA base URL is configured via `REACT_APP_SHARE_URL_BASE`. The
 * convention is:
 *   - dev/test: absolute URL of the Flowenti FE (e.g.
 *     "https://feature2.flowenti.com").
 *   - staging/prod: empty, because the FE and the BE live behind the
 *     same reverse-proxy on the public domain and `window.location.origin`
 *     resolves to the right host.
 *
 * SSR-safe: when `window` is undefined the function falls back to a
 * relative URL.
 *
 * `path` è la coppia rotta-sul-BE/rotta-sullo-SPA sotto cui vive la
 * condivisione: `"ai/shared"` (default, chat AI) oppure `"tasks/shared"`
 * (task di MyActivities — stessa stringa sul BE `/tasks/shared/<hash>` e
 * sulla rotta SPA `/tasks/shared/:slug`, di proposito: sono due path diversi
 * per la stessa feature e tenerli uguali evita di sbagliarli).
 */
export const buildShareUrl = (raw: string, path = "ai/shared"): string => {
  const trimmed = (raw || "").trim();
  if (!trimmed) return "";

  // Step 1 — extract the slug from any supported input shape.
  // The backend may return the share target in any of these forms:
  //   - bare slug:                       "fRgAKKE16BmPUFNJidLRpA"
  //   - leading-slash slug:              "/fRgAKKE16BmPUFNJidLRpA"
  //   - relative path with route:        "/ai/shared/fRgA…"
  //   - BE absolute URL:                 "https://bp2.flowenti.com/ai/share/fRgA…"
  //   - FE absolute URL:                 "https://www.flowenti.com/ai/shared/fRgA…"
  // We always strip any "/ai/shared/" or "/ai/share/" prefix that the
  // BE may have included, then keep the trailing segment as the slug.
  let slug = "";
  if (/^https?:\/\//i.test(trimmed)) {
    try {
      const parsed = new URL(trimmed);
      const segments = parsed.pathname.split("/").filter(Boolean);
      slug = segments[segments.length - 1] || "";
    } catch {
      // Fallback: strip a possible scheme/host by hand.
      const afterScheme = trimmed.replace(/^https?:\/\/[^/]*\//i, "");
      const segments = afterScheme.split("/").filter(Boolean);
      slug = segments[segments.length - 1] || "";
    }
  } else {
    // Strip a leading slash, then a possible "/ai/shared/" or
    // "/ai/share/" prefix, and keep the trailing segment.
    const withoutSlash = trimmed.replace(/^\/+/, "");
    const segments = withoutSlash.split("/").filter(Boolean);
    slug = segments[segments.length - 1] || "";
  }

  if (!slug) return "";

  // Step 2 — build the SPA URL around the slug.
  const baseRaw = process.env.REACT_APP_SHARE_URL_BASE || "";
  const base = baseRaw.replace(/\/+$/, ""); // strip trailing slashes

  // No SPA base configured: best we can do is a relative path on the
  // current origin (HashRouter).
  if (!base) {
    if (typeof window !== "undefined" && window.location?.origin) {
      return `${window.location.origin}/#/${path}/${slug}`;
    }
    return `/#/${path}/${slug}`;
  }

  // Relative base (e.g. "/") → anchor to current origin.
  // Absolute base (e.g. "https://feature2.flowenti.com") → use as-is.
  let absoluteBase: string;
  if (base.startsWith("/")) {
    if (typeof window !== "undefined" && window.location?.origin) {
      absoluteBase = `${window.location.origin}${base}`;
    } else {
      absoluteBase = base;
    }
  } else {
    absoluteBase = base;
  }

  return `${absoluteBase}/#/${path}/${slug}`;
};

/**
 * Build the absolute backend API URL used by `SharedChatPage` to fetch
 * the shared session payload. Kept separate from `buildShareUrl` so
 * the two destinations (the FE the user clicks vs. the BE the FE
 * calls) can be configured independently.
 *
 * The public BE endpoint is
 * `${BE_ORIGIN}/ai/shared/<slug>`. The slug is only "alive" on the
 * BE after `useLLM.shareSession` has issued the authenticated POST
 * `/ai/sessions/.../share` that activates the share (the user does
 * that by clicking the Share button in the AI drawer — visiting
 * `/ai/shared/<slug>` alone does not activate it).
 *
 * `REACT_APP_STATS_BACKEND_BASE_URI` is shared with `aiApi`, whose
 * baseURL is `${STATS_BASE}/ai`. In every env the value ends with
 * `/helpers` (e.g. "https://appserver.flowenti.com/helpers",
 * "/helpers" in staging/prod) because the authenticated
 * `POST /ai/sessions/.../share` is mounted under
 * `${STATS_BASE}/ai/...`. The public GET, instead, lives at
 * `${HOST}/ai/shared/<slug>` — i.e. ONE segment up from the
 * authenticated routes. We therefore strip a trailing `/helpers`
 * before appending `/ai/shared/<slug>` so the public URL stays
 * correct regardless of the env value.
 */
export const buildSharedApiUrl = (slug: string, path = "ai/shared"): string => {
  const trimmed = (slug || "").trim();
  if (!trimmed) return "";
  const baseRaw = process.env.REACT_APP_STATS_BACKEND_BASE_URI || "";
  // Strip a trailing "/helpers" so we land on the host root. We only
  // touch the literal "/helpers" segment to avoid clobbering any
  // other path the env might encode.
  const withoutHelpers = baseRaw.replace(/\/helpers$/, "");
  const base = withoutHelpers.replace(/\/+$/, ""); // strip trailing slashes
  const cleanSlug = trimmed.replace(/^\/+/, "");
  if (!cleanSlug) return "";
  if (!base) {
    if (typeof window !== "undefined" && window.location?.origin) {
      return `${window.location.origin}/${path}/${cleanSlug}`;
    }
    return `/${path}/${cleanSlug}`;
  }
  if (base.startsWith("/")) {
    if (typeof window !== "undefined" && window.location?.origin) {
      return `${window.location.origin}${base}/${path}/${cleanSlug}`;
    }
    return `${base}/${path}/${cleanSlug}`;
  }
  return `${base}/${path}/${cleanSlug}`;
};

/**
 * Alternative backend API URL that keeps the `/helpers` base path.
 * Some production deployments mount the public shared session endpoint
 * under `${STATS_BASE}/ai/shared/<slug>` instead of
 * `${HOST}/ai/shared/<slug>`. `SharedChatPage` uses this as a 404
 * fallback so the same build works in both configurations.
 */
export const buildSharedApiUrlWithHelpers = (
  slug: string,
  path = "ai/shared"
): string => {
  const trimmed = (slug || "").trim();
  if (!trimmed) return "";
  const baseRaw = process.env.REACT_APP_STATS_BACKEND_BASE_URI || "";
  const base = baseRaw.replace(/\/+$/, ""); // keep "/helpers" if present
  const cleanSlug = trimmed.replace(/^\/+/, "");
  if (!cleanSlug) return "";
  if (!base) {
    if (typeof window !== "undefined" && window.location?.origin) {
      return `${window.location.origin}/helpers/${path}/${cleanSlug}`;
    }
    return `/helpers/${path}/${cleanSlug}`;
  }
  if (base.startsWith("/")) {
    if (typeof window !== "undefined" && window.location?.origin) {
      return `${window.location.origin}${base}/${path}/${cleanSlug}`;
    }
    return `${base}/${path}/${cleanSlug}`;
  }
  return `${base}/${path}/${cleanSlug}`;
};
